import { tt } from './i18n'

/**
 * How a payment method is drawn - `FRONTEND.md` §9.204, moved here by §9.384.
 *
 * **Two screens were naming the same six methods.** This lived in `manager-delivery`'s dispatch
 * rules, and the driver app had its own `paymentLabel` - different words, no colour - because
 * `manager-driving` cannot import `manager-delivery`. The owner asked for the driver's badge to
 * *"match colors from Rozvoz nástenka"*, which is a request for one vocabulary and not for a second
 * set of tones. Re-exported from `dispatch-rules.ts`, so every existing call site and its tests are
 * unchanged.
 */
/**
 * Which icon a payment method takes on the board — §9.204.
 *
 * A *kind*, not a component: this file is pure and tested as such, and the call site maps `cash` and
 * `card` to the icons the payment dialog already uses, so the board and the till agree.
 *
 * `other` is a card rather than nothing — every method here that is not cash is taken on a machine,
 * and an unrecognised one is far more likely to be a new card type than a new way of handing over
 * notes.
 *
 * `qr` - §9.741, owner O112: *QR Pay* is paid on the bank's page from the code the driver shows, so
 * neither a banknote nor a card reader is the right picture. lucide's `QrCode` is its icon - the
 * driver's *Zaplatiť prevodom* button already draws it - and a call site that maps only `cash` draws
 * it as a card, which is what it drew before.
 */
export type PaymentIcon = 'cash' | 'card' | 'qr'

/**
 * The tone of money that was in the bank before anybody reached the door - §9.741.
 *
 * `card_online` was paid on CardPay when the order was placed and `qr_pay` on the bank's page at the
 * door; neither is anything for the courier or the till to take. One constant rather than two equal
 * strings, so a later edit to one cannot leave the other behind.
 */
const PAID_ONLINE_TONE = 'bg-blue-500/15 text-blue-400 border-blue-500/30'

export function paymentMethodBadge(
  method: string | undefined,
): { label: string; tone: string; icon: PaymentIcon } | null {
  if (method === undefined || method === '') return null
  const known: Record<string, { label: string; tone: string; icon: PaymentIcon }> = {
    cash: {
      label: tt('Hotovosť'),
      tone: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
      icon: 'cash',
    },
    card_terminal: {
      label: tt('Karta – terminál'),
      tone: 'bg-red-500/15 text-red-400 border-red-500/30',
      icon: 'card',
    },
    card_courier: {
      label: tt('Karta kuriér'),
      tone: 'bg-muted/40 text-muted-foreground border-transparent',
      icon: 'card',
    },
    card_online: {
      label: 'CardPay online',
      tone: PAID_ONLINE_TONE,
      icon: 'card',
    },
    // §10A.390 - a door QR payment is filed as `qr_pay` once the bank says it is paid. It drew the raw
    // key in grey with a card icon, on the board and on the big badge at the door.
    // §9.766, BACKEND.md §10A.418 - and a customer may choose it at checkout, before anything is paid. This badge
    // names the method only; the board's `paymentBadge` and the driver's card say *not paid yet* beside it, which is
    // §9.751's shape for a CardPay the bank never confirmed.
    qr_pay: { label: tt('QR Pay'), tone: PAID_ONLINE_TONE, icon: 'qr' },
    card: { label: tt('Karta'), tone: 'bg-red-500/15 text-red-400 border-red-500/30', icon: 'card' },
    other: {
      label: tt('Iné'),
      tone: 'bg-muted/40 text-muted-foreground border-transparent',
      icon: 'card',
    },
  }
  return (
    known[method] ?? {
      label: method,
      tone: 'bg-muted/40 text-muted-foreground border-transparent',
      icon: 'card',
    }
  )
}
